import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, compose, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';
import {
  Palette, Globe, History, Settings, Save, Upload, RotateCcw,
  Check, AlertCircle, Loader2, Sparkles, X, Clock, User, Eye,
  FileUp, Zap, Bot, Search, Languages
} from 'lucide-react';

import s from './FlutterConfig.css';

// Default config structure - Navega v1.0 Schema
const DEFAULT_CONFIG = {
  version: 1,
  published_at: null,
  theme: {
    colors: {
      primary: '#0066FF',
      secondary: '#00D4AA',
      accent: '#FF6B35',
      background: '#FFFFFF',
      surface: '#F8F9FA',
      text_primary: '#1A1A2E',
      text_secondary: '#6B7280'
    },
    features: {
      dark_mode_enabled: true,
      christmas_theme: false
    }
  },
  translations: {
    es: { home_title: 'Inicio' },
    en: { home_title: 'Home' },
    fr: { home_title: 'Accueil' },
    it: { home_title: 'Home' },
    pt: { home_title: 'Início' },
    ar: { home_title: 'الصفحة الرئيسية' }
  }
};

// Navega v1.0 - All supported languages (EN is SOURCE for Flutter imports)
const SUPPORTED_LANGS = [
  { code: 'en', name: 'English', flag: '🇺🇸', isBase: true },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' }
];

// Navega v1.0 colors
const COLOR_LABELS = {
  primary: 'Primary',
  secondary: 'Secondary',
  accent: 'Accent',
  background: 'Background',
  surface: 'Surface',
  text_primary: 'Text Primary',
  text_secondary: 'Text Secondary'
};

// Navega v1.0 features
const FEATURE_LABELS = {
  dark_mode_enabled: 'Dark Mode',
  christmas_theme: 'Christmas Theme'
};

class FlutterConfig extends Component {
  static propTypes = {
    title: PropTypes.string.isRequired
  };

  state = {
    activeTab: 'theme',
    activeLang: 'en',
    config: { ...DEFAULT_CONFIG },
    originalConfig: null,
    hasChanges: false,
    isLoading: true,
    isSaving: false,
    isPublishing: false,
    isTranslating: false,
    isBulkTranslating: false,
    stopRequested: false,
    bulkProgress: { current: 0, total: 0 },
    translatingKey: null,
    message: null,
    messageType: null,
    showRollbackModal: false,
    rollbackVersionId: null,
    rollbackReason: '',
    newTranslationKey: '',
    newTranslationValue: '',
    searchTerm: '',
    showImportModal: false
  };

  componentDidMount() {
    this.loadConfig();
  }

  loadConfig = async () => {
    this.setState({ isLoading: true });

    try {
      const { data } = await this.props.client.query({
        query: gql`
          query GetFlutterConfigDraft {
            getFlutterConfigDraft
          }
        `,
        fetchPolicy: 'network-only'
      });

      if (data.getFlutterConfigDraft) {
        const config = JSON.parse(data.getFlutterConfigDraft);
        this.setState({
          config,
          originalConfig: JSON.parse(JSON.stringify(config)),
          isLoading: false
        });
      } else {
        this.setState({
          config: { ...DEFAULT_CONFIG },
          originalConfig: { ...DEFAULT_CONFIG },
          isLoading: false
        });
      }
    } catch (error) {
      console.error('Error loading config:', error);
      this.setState({
        isLoading: false,
        message: 'Error loading configuration',
        messageType: 'error'
      });
    }
  };

  handleColorChange = (colorKey, value) => {
    this.setState(prev => ({
      config: {
        ...prev.config,
        theme: {
          ...prev.config.theme,
          colors: {
            ...prev.config.theme.colors,
            [colorKey]: value.toUpperCase()
          }
        }
      },
      hasChanges: true
    }));
  };

  handleFeatureToggle = (featureKey) => {
    this.setState(prev => ({
      config: {
        ...prev.config,
        theme: {
          ...prev.config.theme,
          features: {
            ...prev.config.theme.features,
            [featureKey]: !prev.config.theme.features[featureKey]
          }
        }
      },
      hasChanges: true
    }));
  };


  handleTranslationChange = (lang, key, value) => {
    this.setState(prev => ({
      config: {
        ...prev.config,
        translations: {
          ...prev.config.translations,
          [lang]: {
            ...prev.config.translations[lang],
            [key]: value
          }
        }
      },
      hasChanges: true
    }));
  };

  handleAddTranslation = () => {
    const { newTranslationKey, newTranslationValue, activeLang, config } = this.state;

    if (!newTranslationKey.trim()) return;

    const key = newTranslationKey.trim().toLowerCase().replace(/\s+/g, '_');

    // Add to all languages (Spanish value as base)
    const updatedTranslations = { ...config.translations };
    SUPPORTED_LANGS.forEach(lang => {
      updatedTranslations[lang.code] = {
        ...updatedTranslations[lang.code],
        [key]: lang.code === 'es' ? newTranslationValue : ''
      };
    });

    this.setState(prev => ({
      config: {
        ...prev.config,
        translations: updatedTranslations
      },
      newTranslationKey: '',
      newTranslationValue: '',
      hasChanges: true
    }));
  };

  handleDeleteTranslation = (key) => {
    const { config } = this.state;

    const updatedTranslations = { ...config.translations };
    SUPPORTED_LANGS.forEach(lang => {
      const langTranslations = { ...updatedTranslations[lang.code] };
      delete langTranslations[key];
      updatedTranslations[lang.code] = langTranslations;
    });

    this.setState(prev => ({
      config: {
        ...prev.config,
        translations: updatedTranslations
      },
      hasChanges: true
    }));
  };

  // Flatten nested JSON to dot notation keys
  flattenJSON = (obj, prefix = '') => {
    const result = {};
    for (const key in obj) {
      const newKey = prefix ? `${prefix}.${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        Object.assign(result, this.flattenJSON(obj[key], newKey));
      } else {
        result[newKey] = obj[key];
      }
    }
    return result;
  };

  // Unflatten dot notation keys back to nested JSON
  unflattenJSON = (obj) => {
    const result = {};
    for (const key in obj) {
      const keys = key.split('.');
      let current = result;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = obj[key];
    }
    return result;
  };

  handleImportJSON = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedJSON = JSON.parse(e.target.result);

        // Flatten hierarchical JSON to flat keys (e.g., auth.login.title)
        const flatKeys = this.flattenJSON(importedJSON);

        // Deep copy existing translations
        const currentTrans = this.state.config.translations;
        const updatedTranslations = {
          en: { ...(currentTrans.en || {}) },
          es: { ...(currentTrans.es || {}) },
          fr: { ...(currentTrans.fr || {}) },
          it: { ...(currentTrans.it || {}) },
          pt: { ...(currentTrans.pt || {}) },
          ar: { ...(currentTrans.ar || {}) }
        };

        // Add imported keys - values go to EN (source), others empty
        Object.entries(flatKeys).forEach(([key, value]) => {
          updatedTranslations.en[key] = value;
          if (!updatedTranslations.es[key]) updatedTranslations.es[key] = '';
          if (!updatedTranslations.fr[key]) updatedTranslations.fr[key] = '';
          if (!updatedTranslations.it[key]) updatedTranslations.it[key] = '';
          if (!updatedTranslations.pt[key]) updatedTranslations.pt[key] = '';
          if (!updatedTranslations.ar[key]) updatedTranslations.ar[key] = '';
        });

        this.setState(prev => ({
          config: {
            ...prev.config,
            translations: updatedTranslations
          },
          hasChanges: true,
          showImportModal: false
        }));

        this.showMessage(`Imported ${Object.keys(flatKeys).length} translation keys from JSON`, 'success');
      } catch (error) {
        console.error('Import error:', error);
        this.showMessage(`Import failed: ${error.message}`, 'error');
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  handleAITranslate = async (key) => {
    const { config } = this.state;
    // Source is ENGLISH (EN) - Flutter's original language
    const sourceText = config.translations.en[key];

    if (!sourceText || sourceText.trim() === '') {
      this.showMessage('No English text found for this key', 'warning');
      return;
    }

    this.setState({ isTranslating: true, translatingKey: key });

    try {
      // Use REST endpoint for AI translation - EN → ES, FR, IT, PT
      const response = await fetch('/web-api/admin/translate-ui-message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sourceText,
          sourceLang: 'en',
          targetLangs: ['es', 'fr', 'it', 'pt', 'ar']
        })
      });

      const result = await response.json();

      if (result.success && result.translations) {
        // Deep copy all language objects
        const updatedTranslations = {
          en: { ...(config.translations.en || {}) },
          es: { ...(config.translations.es || {}) },
          fr: { ...(config.translations.fr || {}) },
          it: { ...(config.translations.it || {}) },
          pt: { ...(config.translations.pt || {}) },
          ar: { ...(config.translations.ar || {}) }
        };

        // Update each translated language (ES, FR, IT, PT)
        Object.entries(result.translations).forEach(([lang, text]) => {
          if (updatedTranslations[lang]) {
            updatedTranslations[lang][key] = text;
          }
        });

        this.setState(prev => ({
          config: {
            ...prev.config,
            translations: updatedTranslations
          },
          hasChanges: true,
          isTranslating: false,
          translatingKey: null
        }));

        this.showMessage(`✅ "${key}" translated from EN → ES, FR, IT, PT, AR`, 'success');
      } else {
        throw new Error(result.error || 'Translation failed');
      }
    } catch (error) {
      console.error('Translation error:', error);
      this.setState({ isTranslating: false, translatingKey: null });
      this.showMessage(`Translation failed: ${error.message}`, 'error');
    }
  };

  handleStopBulkTranslate = () => {
    this.setState({ stopRequested: true });
    this.showMessage('Stopping translation...', 'info');
  };

  handleBulkTranslate = async () => {
    const { config } = this.state;
    // Source is ENGLISH (EN) - Flutter's original language
    const enTranslations = config.translations.en || {};

    // Find keys that have English text but missing other translations (ES, FR, IT, PT)
    const keysToTranslate = Object.keys(enTranslations).filter(key => {
      const enText = enTranslations[key];
      if (!enText || enText.trim() === '') return false;

      // Check if any target language is missing
      return ['es', 'fr', 'it', 'pt', 'ar'].some(lang => {
        const text = config.translations[lang]?.[key];
        return !text || text.trim() === '';
      });
    });

    if (keysToTranslate.length === 0) {
      this.showMessage('All translations are complete!', 'success');
      return;
    }

    const confirmed = window.confirm(
      `🤖 AI Bulk Translation (EN → ES, FR, IT, PT, AR)\n\n` +
      `This will translate ${keysToTranslate.length} keys from English to Spanish, French, Italian, Portuguese, Arabic.\n\n` +
      `Estimated time: ~${Math.ceil(keysToTranslate.length * 0.5)} seconds\n\n` +
      `Continue?`
    );

    if (!confirmed) return;

    this.setState({
      isBulkTranslating: true,
      stopRequested: false,
      bulkProgress: { current: 0, total: keysToTranslate.length }
    });

    let successCount = 0;

    for (let i = 0; i < keysToTranslate.length; i++) {
      // Check if stop was requested
      if (this.state.stopRequested) {
        this.showMessage(`⏹️ Stopped. Translated ${successCount} keys before stopping.`, 'info');
        break;
      }

      const key = keysToTranslate[i];
      const sourceText = enTranslations[key];

      this.setState({
        bulkProgress: { current: i + 1, total: keysToTranslate.length },
        translatingKey: key
      });

      try {
        const response = await fetch('/web-api/admin/translate-ui-message', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            sourceText,
            sourceLang: 'en',
            targetLangs: ['es', 'fr', 'it', 'pt', 'ar']
          })
        });

        const result = await response.json();

        if (result.success && result.translations) {
          // Update state immediately after each translation so UI refreshes
          this.setState(prev => {
            // Deep copy all language objects to avoid mutation
            const currentTrans = prev.config.translations;
            const newTranslations = {
              en: { ...(currentTrans.en || {}) },
              es: { ...(currentTrans.es || {}) },
              fr: { ...(currentTrans.fr || {}) },
              it: { ...(currentTrans.it || {}) },
              pt: { ...(currentTrans.pt || {}) },
              ar: { ...(currentTrans.ar || {}) }
            };
            // Apply the new translations
            Object.entries(result.translations).forEach(([lang, text]) => {
              if (newTranslations[lang]) {
                newTranslations[lang][key] = text;
              }
            });
            return {
              config: {
                ...prev.config,
                translations: newTranslations
              },
              hasChanges: true
            };
          });
          successCount++;
        }

        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));

      } catch (error) {
        console.error(`Error translating ${key}:`, error);
      }
    }

    this.setState({
      isBulkTranslating: false,
      stopRequested: false,
      translatingKey: null,
      bulkProgress: { current: 0, total: 0 }
    });

    if (!this.state.stopRequested) {
      this.showMessage(`✅ Translated ${successCount}/${keysToTranslate.length} keys from EN → ES, FR, IT, PT, AR!`, 'success');
    }
  };

  getMissingTranslationsCount = () => {
    const { config } = this.state;
    // Count based on ENGLISH keys (source language)
    const enKeys = Object.keys(config.translations.en || {});
    let missing = 0;

    enKeys.forEach(key => {
      const enText = config.translations.en[key];
      if (!enText || enText.trim() === '') return;

      // Check ES, FR, IT, PT, AR (target languages)
      ['es', 'fr', 'it', 'pt', 'ar'].forEach(lang => {
        const text = config.translations[lang]?.[key];
        if (!text || text.trim() === '') missing++;
      });
    });

    return missing;
  };

  getFilteredTranslationKeys = () => {
    const { config, searchTerm } = this.state;
    // Get keys from ENGLISH (source language)
    const allKeys = Object.keys(config.translations.en || {});

    if (!searchTerm.trim()) return allKeys;

    const term = searchTerm.toLowerCase();
    return allKeys.filter(key => {
      if (key.toLowerCase().includes(term)) return true;
      // Also search in values
      return SUPPORTED_LANGS.some(lang => {
        const value = config.translations[lang.code]?.[key] || '';
        return value.toLowerCase().includes(term);
      });
    });
  };

  handleSaveDraft = async () => {
    const { config } = this.state;

    this.setState({ isSaving: true });

    try {
      const { data } = await this.props.client.mutate({
        mutation: gql`
          mutation SaveFlutterConfigDraft($configJson: String!, $description: String) {
            SaveFlutterConfigDraft(configJson: $configJson, description: $description) {
              status
              errorMessage
              version
              versionId
              isDraft
            }
          }
        `,
        variables: {
          configJson: JSON.stringify(config),
          description: 'Draft saved from admin panel'
        }
      });

      if (data.SaveFlutterConfigDraft.status === 200) {
        this.setState({
          isSaving: false,
          hasChanges: false,
          originalConfig: JSON.parse(JSON.stringify(config))
        });
        this.showMessage(`Draft v${data.SaveFlutterConfigDraft.version} saved!`, 'success');
        this.props.historyData.refetch();
      } else {
        throw new Error(data.SaveFlutterConfigDraft.errorMessage);
      }
    } catch (error) {
      console.error('Save error:', error);
      this.setState({ isSaving: false });
      this.showMessage(`Save failed: ${error.message}`, 'error');
    }
  };

  handlePublish = async () => {
    const { config } = this.state;

    if (!window.confirm('Are you sure you want to publish this configuration to Firebase? This will affect all Flutter app users.')) {
      return;
    }

    this.setState({ isPublishing: true });

    try {
      const { data } = await this.props.client.mutate({
        mutation: gql`
          mutation PublishFlutterConfig($configJson: String!, $description: String) {
            PublishFlutterConfig(configJson: $configJson, description: $description) {
              status
              errorMessage
              version
              versionId
              etag
              publishedAt
              sizeKB
            }
          }
        `,
        variables: {
          configJson: JSON.stringify(config),
          description: 'Published from admin panel'
        }
      });

      if (data.PublishFlutterConfig.status === 200) {
        const result = data.PublishFlutterConfig;
        this.setState({
          isPublishing: false,
          hasChanges: false,
          config: {
            ...config,
            version: result.version
          },
          originalConfig: JSON.parse(JSON.stringify({ ...config, version: result.version }))
        });
        this.showMessage(`v${result.version} published to Firebase! (${result.sizeKB}KB)`, 'success');
        this.props.historyData.refetch();
      } else {
        throw new Error(data.PublishFlutterConfig.errorMessage);
      }
    } catch (error) {
      console.error('Publish error:', error);
      this.setState({ isPublishing: false });
      this.showMessage(`Publish failed: ${error.message}`, 'error');
    }
  };

  handleRollback = async () => {
    const { rollbackVersionId, rollbackReason } = this.state;

    if (!rollbackVersionId) return;

    this.setState({ isPublishing: true });

    try {
      const { data } = await this.props.client.mutate({
        mutation: gql`
          mutation RollbackFlutterConfig($versionId: Int!, $reason: String) {
            RollbackFlutterConfig(versionId: $versionId, reason: $reason) {
              status
              errorMessage
              previousVersion
              newVersion
              rollbackReason
            }
          }
        `,
        variables: {
          versionId: rollbackVersionId,
          reason: rollbackReason
        }
      });

      if (data.RollbackFlutterConfig.status === 200) {
        this.setState({
          isPublishing: false,
          showRollbackModal: false,
          rollbackVersionId: null,
          rollbackReason: ''
        });
        this.showMessage(`Rolled back to v${data.RollbackFlutterConfig.previousVersion}!`, 'success');
        this.loadConfig();
        this.props.historyData.refetch();
      } else {
        throw new Error(data.RollbackFlutterConfig.errorMessage);
      }
    } catch (error) {
      console.error('Rollback error:', error);
      this.setState({ isPublishing: false });
      this.showMessage(`Rollback failed: ${error.message}`, 'error');
    }
  };

  handleLoadVersion = async (versionId) => {
    try {
      const { data } = await this.props.client.query({
        query: gql`
          query GetFlutterConfigVersion($versionId: Int) {
            getFlutterConfigVersion(versionId: $versionId)
          }
        `,
        variables: { versionId },
        fetchPolicy: 'network-only'
      });

      if (data.getFlutterConfigVersion) {
        const config = JSON.parse(data.getFlutterConfigVersion);
        this.setState({
          config,
          hasChanges: true,
          activeTab: 'theme'  // Switch to Theme tab to show loaded config
        });
        this.showMessage(`Loaded v${config.version} for editing`, 'info');
      }
    } catch (error) {
      console.error('Load version error:', error);
      this.showMessage(`Failed to load version: ${error.message}`, 'error');
    }
  };

  showMessage = (message, type) => {
    this.setState({ message, messageType: type });
    setTimeout(() => this.setState({ message: null, messageType: null }), 5000);
  };

  getConfigSizeKB = () => {
    const jsonString = JSON.stringify(this.state.config);
    return Math.round(Buffer.from(jsonString, 'utf8').length / 1024);
  };

  renderThemeTab() {
    const { config } = this.state;
    const { colors, features } = config.theme;

    return (
      <div className={s.mainPanel}>
        {/* Colors Section */}
        <div className={s.card}>
          <div className={s.cardHeader}>
            <h3 className={s.cardTitle}>
              <Palette size={18} />
              Theme Colors
            </h3>
          </div>
          <div className={s.cardBody}>
            <div className={s.colorGrid}>
              {Object.entries(colors).map(([key, value]) => (
                <div key={key} className={s.colorItem}>
                  <label className={s.colorLabel}>{COLOR_LABELS[key] || key}</label>
                  <div className={s.colorInputWrapper}>
                    <input
                      type="color"
                      className={s.colorPreview}
                      value={value}
                      onChange={(e) => this.handleColorChange(key, e.target.value)}
                    />
                    <input
                      type="text"
                      className={s.colorInput}
                      value={value}
                      onChange={(e) => this.handleColorChange(key, e.target.value)}
                      maxLength={7}
                      placeholder="#000000"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className={s.card}>
          <div className={s.cardHeader}>
            <h3 className={s.cardTitle}>
              <Settings size={18} />
              Feature Flags
            </h3>
          </div>
          <div className={s.cardBody}>
            <div className={s.featureGrid}>
              {Object.entries(features).map(([key, value]) => (
                <div key={key} className={s.featureItem}>
                  <span className={s.featureLabel}>{FEATURE_LABELS[key] || key}</span>
                  <div
                    className={cx(s.toggle, { [s.toggleActive]: value })}
                    onClick={() => this.handleFeatureToggle(key)}
                  >
                    <div className={s.toggleKnob} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    );
  }

  renderTranslationsTab() {
    const {
      config, isTranslating, isBulkTranslating, bulkProgress,
      newTranslationKey, newTranslationValue, searchTerm, translatingKey
    } = this.state;
    const translationKeys = this.getFilteredTranslationKeys();
    const missingCount = this.getMissingTranslationsCount();
    const totalKeys = Object.keys(config.translations.en || {}).length;

    return (
      <div className={s.mainPanel}>
        {/* Action Bar */}
        <div className={s.card} style={{ marginBottom: '16px' }}>
          <div className={s.cardBody} style={{ padding: '12px 16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Import JSON Button */}
              <label className={cx(s.btn, s.btnSecondary)} style={{ cursor: 'pointer', margin: 0 }}>
                <FileUp size={16} style={{ marginRight: '6px' }} />
                Import JSON
                <input
                  type="file"
                  accept=".json"
                  onChange={this.handleImportJSON}
                  style={{ display: 'none' }}
                />
              </label>

              {/* Bulk Translate Button */}
              {!isBulkTranslating ? (
                <button
                  className={cx(s.btn, s.btnPrimary)}
                  onClick={this.handleBulkTranslate}
                  disabled={missingCount === 0}
                  style={{ background: missingCount > 0 ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : undefined }}
                >
                  <Bot size={16} style={{ marginRight: '6px' }} />
                  AI Translate Missing ({missingCount})
                </button>
              ) : (
                <>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px'
                  }}>
                    <Loader2 size={16} className={s.spinner} />
                    <span>Translating {bulkProgress.current}/{bulkProgress.total}</span>
                  </div>
                  <button
                    className={cx(s.btn, s.btnDanger)}
                    onClick={this.handleStopBulkTranslate}
                    style={{ background: '#ef4444' }}
                  >
                    <X size={16} style={{ marginRight: '4px' }} />
                    STOP
                  </button>
                </>
              )}

              {/* Search */}
              <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
                <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => this.setState({ searchTerm: e.target.value })}
                  placeholder="Search keys or values..."
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 36px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Stats */}
              <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#6b7280' }}>
                <span><strong>{totalKeys}</strong> keys</span>
                <span style={{ color: missingCount > 0 ? '#f59e0b' : '#10b981' }}>
                  <strong>{missingCount}</strong> missing
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Translations Table - All Languages Visible */}
        <div className={s.card}>
          <div className={s.cardHeader}>
            <h3 className={s.cardTitle}>
              <Languages size={18} />
              Translations
              {translatingKey && (
                <span style={{ marginLeft: '12px', fontSize: '12px', color: '#6366f1' }}>
                  <Loader2 size={12} className={s.spinner} style={{ marginRight: '4px' }} />
                  {translatingKey}
                </span>
              )}
            </h3>
          </div>
          <div className={s.cardBody} style={{ padding: 0 }}>
            {translationKeys.length === 0 ? (
              <div className={s.emptyState} style={{ padding: '40px' }}>
                <Globe size={48} />
                <p>{searchTerm ? 'No translations match your search.' : 'No translations yet. Import a JSON or add keys below.'}</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', minWidth: '180px', position: 'sticky', left: 0, background: '#f8fafc', zIndex: 1 }}>
                        Key
                      </th>
                      {SUPPORTED_LANGS.map(lang => (
                        <th key={lang.code} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', minWidth: '200px' }}>
                          {lang.flag} {lang.code.toUpperCase()}
                          {lang.isBase && <span style={{ marginLeft: '4px', fontSize: '10px', color: '#6366f1' }}>(SOURCE)</span>}
                        </th>
                      ))}
                      <th style={{ padding: '12px 16px', textAlign: 'center', width: '80px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {translationKeys.map((key, idx) => {
                      const enValue = config.translations.en?.[key] || '';
                      const hasEnText = enValue.trim() !== '';
                      const isCurrentlyTranslating = translatingKey === key;

                      return (
                        <tr
                          key={key}
                          style={{
                            borderBottom: '1px solid #e5e7eb',
                            background: isCurrentlyTranslating ? 'rgba(99, 102, 241, 0.08)' :
                              idx % 2 === 0 ? '#ffffff' : '#fafafa'
                          }}
                        >
                          {/* Key Column */}
                          <td style={{
                            padding: '8px 16px',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            color: '#374151',
                            position: 'sticky',
                            left: 0,
                            background: isCurrentlyTranslating ? 'rgba(99, 102, 241, 0.08)' :
                              idx % 2 === 0 ? '#ffffff' : '#fafafa',
                            zIndex: 1,
                            borderRight: '1px solid #e5e7eb'
                          }}>
                            {key}
                          </td>

                          {/* Language Columns */}
                          {SUPPORTED_LANGS.map(lang => {
                            const value = config.translations[lang.code]?.[key] || '';
                            const isMissing = lang.code !== 'en' && hasEnText && value.trim() === '';

                            return (
                              <td key={lang.code} style={{ padding: '4px 8px' }}>
                                <input
                                  type="text"
                                  value={value}
                                  onChange={(e) => this.handleTranslationChange(lang.code, key, e.target.value)}
                                  placeholder={lang.isBase ? 'English source...' : `${lang.code.toUpperCase()}...`}
                                  style={{
                                    width: '100%',
                                    padding: '8px 10px',
                                    border: `1px solid ${isMissing ? '#f59e0b' : '#e5e7eb'}`,
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    background: isMissing ? 'rgba(245, 158, 11, 0.05)' :
                                      isCurrentlyTranslating ? 'rgba(99, 102, 241, 0.05)' : '#fff'
                                  }}
                                />
                              </td>
                            );
                          })}

                          {/* Actions Column */}
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                className={s.aiTranslateBtn}
                                onClick={() => this.handleAITranslate(key)}
                                disabled={isTranslating || !hasEnText}
                                title={hasEnText ? 'AI Translate' : 'No EN text'}
                                style={{ opacity: hasEnText ? 1 : 0.4, padding: '6px 8px' }}
                              >
                                {isCurrentlyTranslating ? (
                                  <Loader2 size={14} className={s.spinner} />
                                ) : (
                                  <Sparkles size={14} />
                                )}
                              </button>
                              <button
                                onClick={() => this.handleDeleteTranslation(key)}
                                title="Delete"
                                style={{
                                  padding: '6px 8px',
                                  border: 'none',
                                  background: 'none',
                                  cursor: 'pointer',
                                  color: '#ef4444'
                                }}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Add New Translation */}
            <div style={{
              display: 'flex',
              gap: '12px',
              padding: '16px',
              borderTop: '1px solid #e5e7eb',
              background: '#f8fafc'
            }}>
              <input
                type="text"
                value={newTranslationKey}
                onChange={(e) => this.setState({ newTranslationKey: e.target.value })}
                placeholder="Key (e.g., auth.login.title)"
                style={{ flex: 2, padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px' }}
              />
              <input
                type="text"
                value={newTranslationValue}
                onChange={(e) => this.setState({ newTranslationValue: e.target.value })}
                placeholder="English value"
                style={{ flex: 3, padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px' }}
              />
              <button
                className={cx(s.btn, s.btnPrimary)}
                onClick={this.handleAddTranslation}
                disabled={!newTranslationKey.trim()}
              >
                Add Key
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderHistoryTab() {
    const { historyData } = this.props;
    const history = historyData?.getFlutterConfigHistory || [];

    return (
      <div className={s.mainPanel}>
        <div className={s.card}>
          <div className={s.cardHeader}>
            <h3 className={s.cardTitle}>
              <History size={18} />
              Version History
            </h3>
          </div>
          <div className={s.cardBody}>
            {history.length === 0 ? (
              <div className={s.emptyState}>
                <History size={48} />
                <p>No versions published yet.</p>
              </div>
            ) : (
              <div className={s.historyList}>
                {history.map(version => (
                  <div
                    key={version.id}
                    className={cx(s.historyItem, { [s.historyItemActive]: version.isActive })}
                  >
                    <div className={s.historyInfo}>
                      <div className={s.historyVersion}>
                        v{version.version}
                        {version.isActive && (
                          <span className={cx(s.statusBadge, s.statusActive)}>
                            <Check size={12} /> Active
                          </span>
                        )}
                        {version.isDraft && (
                          <span className={cx(s.statusBadge, s.statusDraft)}>
                            Draft
                          </span>
                        )}
                      </div>
                      <div className={s.historyDate}>
                        <Clock size={12} style={{ marginRight: '4px' }} />
                        {version.publishedAt
                          ? new Date(version.publishedAt).toLocaleString()
                          : 'Not published'}
                        {version.changeDescription && ` - ${version.changeDescription}`}
                      </div>
                    </div>
                    <div className={s.historyActions}>
                      <button
                        className={cx(s.btn, s.btnSmall, s.btnSecondary)}
                        onClick={() => this.handleLoadVersion(version.id)}
                        title="Load this version for editing"
                      >
                        <Eye size={14} />
                      </button>
                      {!version.isActive && !version.isDraft && (
                        <button
                          className={cx(s.btn, s.btnSmall, s.btnDanger)}
                          onClick={() => this.setState({
                            showRollbackModal: true,
                            rollbackVersionId: version.id
                          })}
                          title="Rollback to this version"
                        >
                          <RotateCcw size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderSidePanel() {
    const { config, hasChanges } = this.state;
    const sizeKB = JSON.stringify(config).length / 1024;
    const sizePercent = Math.min((sizeKB / 800) * 100, 100);

    return (
      <div className={s.sidePanel}>
        {/* Preview Card */}
        <div className={s.previewCard}>
          <div className={s.previewHeader}>
            <span className={s.previewTitle}>NAVEGA App</span>
          </div>
          <div className={s.previewColors}>
            {Object.entries(config.theme.colors).slice(0, 6).map(([key, value]) => (
              <div
                key={key}
                className={s.previewColorDot}
                style={{ backgroundColor: value }}
                title={`${key}: ${value}`}
              />
            ))}
          </div>
        </div>

        {/* Size Indicator */}
        <div className={s.card}>
          <div className={s.cardBody}>
            <div className={s.sizeIndicator}>
              <span>Config Size</span>
              <div className={s.sizeBar}>
                <div
                  className={s.sizeBarFill}
                  style={{ width: `${sizePercent}%` }}
                />
              </div>
              <span>{sizeKB.toFixed(1)} / 800 KB</span>
            </div>
          </div>
        </div>

        {/* JSON Preview */}
        <div className={s.card}>
          <div className={s.cardHeader}>
            <h3 className={s.cardTitle}>JSON Preview</h3>
          </div>
          <div className={s.jsonPreview}>
            <pre>{JSON.stringify(config, null, 2).substring(0, 1000)}...</pre>
          </div>
        </div>
      </div>
    );
  }

  renderRollbackModal() {
    const { showRollbackModal, rollbackReason, isPublishing } = this.state;

    if (!showRollbackModal) return null;

    return (
      <div className={s.modal}>
        <div className={s.modalContent}>
          <div className={s.modalHeader}>
            <h3 className={s.modalTitle}>Rollback Configuration</h3>
            <button
              className={cx(s.btn, s.btnIcon)}
              onClick={() => this.setState({ showRollbackModal: false })}
            >
              <X size={20} />
            </button>
          </div>
          <div className={s.modalBody}>
            <p style={{ marginBottom: '16px', color: '#6c757d' }}>
              This will publish the selected version to Firebase, replacing the current active configuration.
            </p>
            <div className={s.formGroup}>
              <label className={s.formLabel}>Reason for rollback (optional)</label>
              <textarea
                className={cx(s.formInput, s.formTextarea)}
                value={rollbackReason}
                onChange={(e) => this.setState({ rollbackReason: e.target.value })}
                placeholder="e.g., Bug in latest version, reverting to stable..."
              />
            </div>
          </div>
          <div className={s.modalFooter}>
            <button
              className={cx(s.btn, s.btnSecondary)}
              onClick={() => this.setState({ showRollbackModal: false })}
            >
              Cancel
            </button>
            <button
              className={cx(s.btn, s.btnDanger)}
              onClick={this.handleRollback}
              disabled={isPublishing}
            >
              {isPublishing ? (
                <>
                  <Loader2 size={16} className={s.spinner} />
                  Rolling back...
                </>
              ) : (
                <>
                  <RotateCcw size={16} />
                  Confirm Rollback
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {
      activeTab, config, hasChanges, isLoading, isSaving, isPublishing, message, messageType
    } = this.state;

    if (isLoading) {
      return (
        <div className={s.container}>
          <div className={s.emptyState}>
            <Loader2 size={48} className={s.spinner} />
            <p>Loading configuration...</p>
          </div>
        </div>
      );
    }

    return (
      <div className={s.container}>
        {/* Header */}
        <div className={s.header}>
          <h1 className={s.title}>
            <Settings size={28} />
            Flutter Remote Config
            {config.version > 0 && (
              <span className={s.versionBadge}>
                v{config.version}
              </span>
            )}
          </h1>
          <div className={s.headerActions}>
            <button
              className={cx(s.btn, s.btnSecondary)}
              onClick={this.handleSaveDraft}
              disabled={!hasChanges || isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className={s.spinner} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Draft
                </>
              )}
            </button>
            <button
              className={cx(s.btn, s.btnPrimary)}
              onClick={this.handlePublish}
              disabled={isPublishing}
            >
              {isPublishing ? (
                <>
                  <Loader2 size={16} className={s.spinner} />
                  Publishing...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Publish to Firebase
                </>
              )}
            </button>
          </div>
        </div>

        {/* Messages */}
        {message && (
          <div className={cx(s.alert, {
            [s.alertSuccess]: messageType === 'success',
            [s.alertError]: messageType === 'error',
            [s.alertWarning]: messageType === 'warning',
            [s.alertInfo]: messageType === 'info'
          })}>
            {messageType === 'success' && <Check size={18} />}
            {messageType === 'error' && <AlertCircle size={18} />}
            {messageType === 'warning' && <AlertCircle size={18} />}
            {messageType === 'info' && <AlertCircle size={18} />}
            {message}
          </div>
        )}

        {/* Tabs */}
        <div className={s.tabs}>
          <button
            className={cx(s.tab, { [s.tabActive]: activeTab === 'theme' })}
            onClick={() => this.setState({ activeTab: 'theme' })}
          >
            <Palette size={18} />
            Theme & Features
          </button>
          <button
            className={cx(s.tab, { [s.tabActive]: activeTab === 'translations' })}
            onClick={() => this.setState({ activeTab: 'translations' })}
          >
            <Globe size={18} />
            Translations
          </button>
          <button
            className={cx(s.tab, { [s.tabActive]: activeTab === 'history' })}
            onClick={() => this.setState({ activeTab: 'history' })}
          >
            <History size={18} />
            History
          </button>
        </div>

        {/* Content */}
        <div className={s.grid}>
          {activeTab === 'theme' && this.renderThemeTab()}
          {activeTab === 'translations' && this.renderTranslationsTab()}
          {activeTab === 'history' && this.renderHistoryTab()}

          {activeTab !== 'history' && this.renderSidePanel()}
        </div>

        {/* Rollback Modal */}
        {this.renderRollbackModal()}
      </div>
    );
  }
}

export default compose(
  withStyles(s),
  withApollo,
  graphql(gql`
    query GetFlutterConfigHistory {
      getFlutterConfigHistory(limit: 20) {
        id
        version
        publishedAt
        publishedBy
        publisherEmail
        isActive
        isDraft
        changeDescription
        rollbackReason
        createdAt
      }
    }
  `, {
    name: 'historyData',
    options: {
      fetchPolicy: 'network-only'
    }
  })
)(FlutterConfig);
